// 付箋の署名（最終更新者名 updatedByName）を更新してよい条件を、サーバ・クライアントで共有する。

import type { CanvasObject } from "./canvas-object";

/**
 * 付箋の署名（updatedByName）を、本文（text）の文字列が変化した更新のときだけ
 * 操作者の表示名で更新する。
 *
 * 付箋の UI で唯一可視化される人物情報が updatedByName であるため、位置移動・リサイズ・
 * 色変更など本文を変えない操作でこれを書き換えると、内容を書いていない人の名前が表示される。
 * そのため「本文の文字列が変わったか」だけを判定材料にする。
 *
 * 判定は変更前スナップショット（previous）と適用後（next）の text 比較で行い、op の種類には
 * 依存しない。サーバの op 適用とクライアントの楽観適用が同じ結果になるよう、両者がこの関数を
 * 共有する（結果がずれると、楽観反映とサーバ確定で署名がちらつく）。
 *
 * actorDisplayName が空 / 未指定のときは現状値を維持する（旧クライアント由来の op で
 * 名前が消えないようにする既存方針を踏襲）。付箋以外はそのまま返す。
 *
 * 新規作成（object.create）では使わない。作成経路はクライアント申告値を一切採らない
 * 厳格上書きが必要で、別のヘルパーが担当する。
 */
export function applyStickyAuthorshipOnTextChange(
  previous: CanvasObject,
  next: CanvasObject,
  actorDisplayName: string | undefined
): CanvasObject {
  if (next.type !== "sticky") return next;
  if (!isStickyTextChanged(previous, next.text)) return next;
  if (typeof actorDisplayName !== "string" || actorDisplayName.length === 0) return next;
  if (next.updatedByName === actorDisplayName) return next;
  return { ...next, updatedByName: actorDisplayName };
}

/**
 * 付箋の本文（text）が、この更新で実際に変化するかを判定する。
 *
 * 「署名を更新してよい条件」の唯一の定義であり、op 経路（applyStickyAuthorshipOnTextChange 経由）と
 * REST / MCP 経路（本関数を直接呼ぶ）が同じ規則を共有するために切り出している。条件を変えるときは
 * ここだけを変えれば 3 経路すべてに反映される。
 *
 * current が付箋でない場合と、本文の指定が無い更新（移動・リサイズ・色変更だけの更新）は false。
 */
export function isStickyTextChanged(
  current: CanvasObject,
  nextText: string | undefined
): boolean {
  if (current.type !== "sticky") return false;
  if (nextText === undefined) return false;
  return current.text !== nextText;
}
